import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "tutorial-introduction"
    }}>{`Tutorial Introduction`}</h1>
    <p>{`The Cloud Manager API Tutorial walks through the process of creating an integration between Cloud Manager and either Microsoft Teams or Slack.`}</p>
    <p>{`The files used in this tutorial can all be found in GitHub in the repository at `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/cloudmanager-api-tutorial"
      }}>{`https://github.com/AdobeDocs/cloudmanager-api-tutorial`}</a>{`. The result of each tutorial step is in a separate file:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Step`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`File`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Basic Webhook Setup`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step1.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Webhook Signature Validation`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step2.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Looking for Specific Event Type`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step3.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Getting an Access Token`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step4.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`5`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Getting Execution Data`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step5.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`6`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Getting Program Data`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step6.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`7`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notifying Slack`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step7-slack.js`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`7`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notifying Microsoft Teams`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`step7-teams.js`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`Before starting the tutorial, you must first set up an project in the `}<a href="https://developer.adobe.com/console/projects" target="_new">{`Adobe Developer Console`}</a>{`. First, follow the instructions on the `}<a parentName="p" {...{
        "href": "/experience-cloud/cloud-manager/guides/getting-started/create-api-integration/"
      }}>{`Creating an API Integration`}</a>{` to create an API integration. You'll add the Event Provider integration in a later step.`}</p>
    <h2 {...{
      "id": "project-initialization"
    }}>{`Project Initialization`}</h2>
    <h3 {...{
      "id": "configuration-variables"
    }}>{`Configuration Variables`}</h3>
    <p>{`This tutorial does involve coding, so you're going to want to open up your favorite IDE now. Create a new project in your IDE with an empty folder. In this project, create a file named `}<inlineCode parentName="p">{`.env`}</inlineCode>{`. This file is going to hold various configuration variables which should be kept out of the code.`}</p>
    <p>{`Populate this file with the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`PORT=4000
ORGANIZATION_ID=
CLIENT_ID=
CLIENT_SECRET=
GRANT_TYPE=client_credentials
SCOPES=
`}</code></pre>
    <p>{`Let's go through each of these and set them.`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`PORT`}</inlineCode>{` -- this is the port on which the webhook will listen. 4000 is a good default value, unless something else is using this port.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`ORGANIZATION_ID`}</inlineCode>{` -- this can be found in the Credentials details section of the Adobe Developer Console.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`CLIENT_ID`}</inlineCode>{` -- this can be found in the Credentials details section of the Adobe Developer Console.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`CLIENT_SECRET`}</inlineCode>{` -- this can be found in the Credentials details section of the Adobe Developer Console. Note that you have to click the `}<inlineCode parentName="li">{`Retrieve client secret`}</inlineCode>{` button to reveal this.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`GRANT_TYPE`}</inlineCode>{` -- this is equal to `}<strong parentName="li">{`client_credentials`}</strong>{`.`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`SCOPES`}</inlineCode>{` -- this can be found in the Credentials details section of the Adobe Developer Console.`}</li>
    </ol>
    <h3 {...{
      "id": "nodejs-installation"
    }}>{`Node.js Installation`}</h3>
    <p>{`This tutorial is written in JavaScript using `}<a href="https://nodejs.org/" target="_new">{`Node.js`}</a>{`. If you don't have Node.js installed, now is the time to do so. You'll also need npm, the Node Package Manager, as this tutorial uses a number of third-party dependencies, distributed as Node packages. Most of the time, npm is installed automatically when you install Node.js.`}</p>
    <h3 {...{
      "id": "nodejs-initialization"
    }}>{`Node.js Initialization`}</h3>
    <p>{`In order to use the Node packages dependened upon, you'll need to create a basic `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file. To do this, open up a Terminal/Shell window in the project directory and run`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm init -y
`}</code></pre>
    <p>{`from the Terminal or Shell.`}</p>
    <h3 {...{
      "id": "next-step"
    }}>{`Next Step`}</h3>
    <p>{`With all that done, you're ready to start the actual tutorial. Continue to `}<a parentName="p" {...{
        "href": "1-a-basic-webhook.md"
      }}>{`Step 1`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      